import { useCallback, useEffect, useState } from 'react';
import { useSnackbarProvider } from 'src/hooks';
import api from '../api';
import { IOwner } from '../ts/interfaces/Owner';

interface UseOwnerListReturn {
  // State
  owners: IOwner[];
  isLoading: boolean;
  error: string | null;
  count: number;
  page: number;
  rowsPerPage: number;
  searchQuery: string;

  // Status update state
  isUpdatingStatus: boolean;
  statusUpdateError: string | null;

  // Actions
  onGetAll: (page?: number, rowsPerPage?: number) => Promise<void>;
  onSearch: (query: string) => void;
  onUpdateStatus: (id: string, status: 'ACTIVE' | 'DISABLED') => Promise<void>;
  onResetStatusUpdate: () => void;

  // Computed properties
  isToolbarDisabled: boolean;
  hasData: boolean;
}

const useOwnerList = (): UseOwnerListReturn => {
  // State management
  const [owners, setOwners] = useState<IOwner[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');

  // Status update state
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [statusUpdateError, setStatusUpdateError] = useState<string | null>(null);

  const { SnackTypes, showSnackbar, VariantType } = useSnackbarProvider();

  // Computed properties
  const isToolbarDisabled = isLoading || owners.length === 0;
  const hasData = owners.length > 0;

  // Fetch owners
  const onGetAll = useCallback(
    async (pageNumber: number = page, perPage: number = rowsPerPage) => {
      setIsLoading(true);
      setError(null);
      try {
        const basePayload = {
          page: pageNumber,
          rowsPerPage: perPage
        };
        const payload = searchQuery ? Object.assign({ q: searchQuery }, basePayload) : basePayload;
        const response = await api.getAllOwners(payload);
        setOwners(response.data as IOwner[]);
        setCount(response.count || 0);
        setPage(pageNumber);
        setRowsPerPage(perPage);
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch owners';
        setError(errorMessage);
        setOwners([]);
        setCount(0);
      } finally {
        setIsLoading(false);
      }
    },
    [page, rowsPerPage, searchQuery]
  );

  // Attempt to get all the owners on the initial load
  useEffect(() => {
    onGetAll();
  }, [onGetAll, searchQuery]);

  // Search owners
  const onSearch = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  // Update owner status
  const onUpdateStatus = useCallback(
    async (id: string, status: 'ACTIVE' | 'DISABLED') => {
      setIsUpdatingStatus(true);
      setStatusUpdateError(null);
      try {
        await api.updateOwnerStatus(id, status);
        showSnackbar(VariantType.success, `Owner status updated to ${status}`, SnackTypes.none);
        // Refresh list after status update
        onGetAll(page, rowsPerPage);
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to update owner status';
        setStatusUpdateError(errorMessage);
        showSnackbar(VariantType.error, errorMessage, SnackTypes.none);
      } finally {
        setIsUpdatingStatus(false);
      }
    },
    [page, rowsPerPage, onGetAll, showSnackbar, SnackTypes, VariantType]
  );

  const onResetStatusUpdate = useCallback(() => {
    setIsUpdatingStatus(false);
    setStatusUpdateError(null);
  }, []);

  return {
    // State
    owners,
    isLoading,
    error,
    count,
    page,
    rowsPerPage,
    searchQuery,

    // Status update state
    isUpdatingStatus,
    statusUpdateError,

    // Actions
    onGetAll,
    onSearch,
    onUpdateStatus,
    onResetStatusUpdate,

    // Computed properties
    isToolbarDisabled,
    hasData
  };
};

export default useOwnerList;
